import { all, call, put, takeLatest } from 'redux-saga/effects'
import * as actions from './actions'
import * as api from './api'
import { getSortedIds } from 'helpers/jsonAPI'
import { message } from 'antd'

function* fetchArcAdsWorker({ payload }) {
  try {
    const { data } = yield call(api.getArcAds, payload)
    if (data) {
      yield put(
        actions.fetchArcAdsSuccess({
          ...payload,
          ads: data.data,
          included: data.included,
          sortedIds: getSortedIds(data.data),
          pageCount: data.meta?.pagination?.pages || 1,
        })
      )
    }
  } catch (error) {
    console.error(error)
    yield put(actions.fetchArcAdsFailure())
  }
}

function* fetchArcAdDetailsWorker({ payload }) {
  try {
    const { data } = yield call(api.getArcAd, payload.id)
    if (data) {
      yield put(
        actions.fetchArcAdDetailsSuccess({
          ad: data.data,
          included: data.included || [],
        })
      )
    }
  } catch (error) {
    console.error(error)
    yield put(actions.fetchArcAdDetailsFailure())
  }
}

function* fetchArcAdSourcesWorker({ payload }) {
  try {
    const { data } = yield call(api.getArcAdSources)
    if (data) {
      yield put(
        actions.fetchArcAdSourcesSuccess({
          sources: data || [],
        })
      )
    }
  } catch (error) {
    console.error(error)
    yield put(actions.fetchArcAdSourcesFailure())
  }
}

function* fetchArcCategoriesWorker({ payload }) {
  try {
    const { data } = yield call(api.getArcCategories)
    if (data) {
      yield put(
        actions.fetchArcCategoriesSuccess({
          categories: data.data || [],
        })
      )
    }
  } catch (error) {
    console.error(error)
    yield put(actions.fetchArcCategoriesFailure())
  }
}

function* createArcAdWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const { data } = yield call(api.createArcAd, payload.data)
    if (data) {
      message.success('Arc ad created successfully')
      if (payload.callback) {
        payload.callback()
      }
    } else {
      message.error('Failed to create arc ad')
    }
  } catch (error) {
    console.error(error)
    message.error('Failed to create arc ad')
  } finally {
    yield put(actions.finishSaving())
  }
}

function* updateArcAdWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const { data } = yield call(api.updateArcAd, payload.data)
    if (data) {
      // Skip Redux update for image removal operations to prevent data corruption
      const isImageRemoval = payload.data.attributes?.clear_icon_image ||
                             payload.data.attributes?.clear_hero_image ||
                             payload.data.attributes?.clear_logo_icon_image

      if (!isImageRemoval) {
        yield put(actions.updateArcAdSuccess(payload))
        message.success('Arc ad updated successfully')
      }

      if (payload.callback) {
        payload.callback()
      }
    } else {
      yield put(actions.updateArcAdFailure())
      message.error('Failed to update arc ad')
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateArcAdFailure())
    message.error('Failed to update arc ad')
  } finally {
    yield put(actions.finishSaving())
  }
}

function* deleteArcAdWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    yield call(api.deleteArcAd, payload.id)
    message.success('Arc ad deleted successfully')
    if (payload.callback) {
      payload.callback()
    }
  } catch (error) {
    console.error(error)
    message.error('Failed to delete arc ad')
  } finally {
    yield put(actions.finishSaving())
  }
}

function* cloneArcAdWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const { data } = yield call(api.cloneArcAd, payload.id)
    if (data) {
      message.success('Arc ad cloned successfully')
      if (payload.callback) {
        payload.callback()
      }
    } else {
      message.error('Failed to clone arc ad')
    }
  } catch (error) {
    console.error(error)
    message.error('Failed to clone arc ad')
  } finally {
    yield put(actions.finishSaving())
  }
}

function* fetchAffiliateNetworksWorker({ payload }) {
  try {
    const { data } = yield call(api.getAffiliateNetworks)
    if (data) {
      yield put(
        actions.fetchAffiliateNetworksSuccess({
          networks: data.networks || [],
        })
      )
    }
  } catch (error) {
    console.error(error)
    yield put(actions.fetchAffiliateNetworksFailure())
  }
}

function* convertClickUrlWorker({ payload }) {
  try {
    yield put(actions.startConverting())
    const { data } = yield call(api.convertClickUrl, payload)
    if (data) {
      yield put(
        actions.convertClickUrlSuccess({
          convertedUrl: data.link,
        })
      )
      message.success('URL converted successfully')
    } else {
      yield put(actions.convertClickUrlFailure({ error: 'Failed to convert URL' }))
      message.error('Failed to convert URL')
    }
  } catch (error) {
    console.error(error)
    const errorMessage = error.response?.data?.message || 'Failed to convert URL'
    yield put(actions.convertClickUrlFailure({ error: errorMessage }))
    message.error(errorMessage)
  } finally {
    yield put(actions.finishConverting())
  }
}

export default function* () {
  yield all([
    takeLatest(actions.fetchArcAds, fetchArcAdsWorker),
    takeLatest(actions.fetchArcAdDetails, fetchArcAdDetailsWorker),
    takeLatest(actions.fetchArcAdSources, fetchArcAdSourcesWorker),
    takeLatest(actions.fetchArcCategories, fetchArcCategoriesWorker),
    takeLatest(actions.createArcAd, createArcAdWorker),
    takeLatest(actions.updateArcAd, updateArcAdWorker),
    takeLatest(actions.deleteArcAd, deleteArcAdWorker),
    takeLatest(actions.cloneArcAd, cloneArcAdWorker),
    takeLatest(actions.fetchAffiliateNetworks, fetchAffiliateNetworksWorker),
    takeLatest(actions.convertClickUrl, convertClickUrlWorker),
  ])
}